import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "@noom/hive-auth";
import axios from "axios";

import { patient, PatientExistsResponse } from "@modules/authentication/Patient";
import { RUM_ACTIONS } from "@app/rumMonitoring";
import { trackDatadogAction } from "@app/datadog";

/**
 * Patient metadata that used to live inside the localStorage `noomUserLogin`
 * blob. With hive-auth owning the token/session, this product/UX metadata is
 * fetched separately (Growth Insurance patient API) and exposed here.
 */
export type PatientProfile = PatientExistsResponse;

/**
 * Resolution state of the patient-profile fetch:
 * - `loading`     — auth present, fetch in flight (or not started yet)
 * - `patient`     — 200, `profile` populated
 * - `not-patient` — authenticated but the patient API returned 400/404
 *                   (valid token, not enrolled in the telehealth program)
 * - `error`       — the fetch failed for another reason
 */
export type PatientProfileStatus =
  | "loading"
  | "patient"
  | "not-patient"
  | "error";

export type PatientProfileContextValue = {
  profile: PatientProfile | null;
  isLoading: boolean;
  error: Error | null;
  status: PatientProfileStatus;
};

// Matches the pre-migration "no patient for this user" (HTTP 400) message.
// Consumed by `RequirePatient`, which enforces the telehealth-patient gate on
// routes that opt in. (This provider no longer performs that side effect — it
// only fetches + reports status — so the gate can be relaxed per-route.)
export const NOT_ENROLLED_MESSAGE =
  "Could not confirm enrollment in telehealth program. Please check you entered your information correctly. Otherwise please contact Support@noom.com";

// Persisted patient profile, mirroring the pre-migration localStorage
// `noomUserLogin` behavior: the profile is fetched once per access token and
// cached so a full reload (notably each native WebView open of the Care tab)
// hydrates synchronously instead of re-fetching `/v1/patient/` and showing a
// loader. Keyed by access token so a different user / rotated token misses the
// cache and re-fetches; cleared on logout via `clearPatientProfileCache()`.
const PATIENT_PROFILE_CACHE_KEY = "noomPatientProfile";

// Cached results expire after this long, mirroring the legacy ~10-minute
// localStorage expiry. Long enough to keep reloads / WebView reopens fast, but
// short enough that a stale result is re-fetched soon — without this, a
// `not-patient` who later enrolls (same long-lived token) would stay cached as
// `not-patient` forever and never see the Care tab / gated routes.
const PATIENT_PROFILE_CACHE_TTL_MS = 10 * 60 * 1000;

// Only resolved (non-transient) results are cached. `error` is intentionally
// never persisted so a transient failure retries on the next load.
type CachedPatientProfile = {
  accessToken: string;
  status: "patient" | "not-patient";
  profile: PatientProfile | null;
  // Epoch ms after which the entry is stale and must be re-fetched.
  expiresAt: number;
};

const readCache = (
  accessToken: string | undefined
): CachedPatientProfile | null => {
  if (!accessToken) return null;
  try {
    const raw = localStorage.getItem(PATIENT_PROFILE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPatientProfile;
    // Ignore a cache that belongs to a different token (user switch / refresh).
    if (parsed.accessToken !== accessToken) return null;
    // Ignore an expired entry (or an old, pre-TTL format with no `expiresAt`)
    // so enrollment changes and other stale metadata are re-fetched.
    if (typeof parsed.expiresAt !== "number" || Date.now() > parsed.expiresAt) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (entry: Omit<CachedPatientProfile, "expiresAt">): void => {
  try {
    const withExpiry: CachedPatientProfile = {
      ...entry,
      expiresAt: Date.now() + PATIENT_PROFILE_CACHE_TTL_MS,
    };
    localStorage.setItem(PATIENT_PROFILE_CACHE_KEY, JSON.stringify(withExpiry));
  } catch {
    // Storage unavailable (private mode / quota) — degrade to re-fetching.
  }
};

export const clearPatientProfileCache = (): void => {
  try {
    localStorage.removeItem(PATIENT_PROFILE_CACHE_KEY);
  } catch {
    // Nothing actionable if storage is unavailable.
  }
};

const defaultValue: PatientProfileContextValue = {
  profile: null,
  isLoading: false,
  error: null,
  status: "loading",
};

const PatientProfileContext =
  createContext<PatientProfileContextValue>(defaultValue);

export const PatientProfileProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isAuthenticated, user, shouldReLogin } = useAuth();
  const accessToken = user?.accessToken;

  // hive-auth keeps `isAuthenticated` true as long as the auth cookie exists,
  // even after the token has expired — only `shouldReLogin()` flips at expiry.
  // The profile represents a *valid* authenticated patient, so an expired
  // session must not expose it: otherwise stale profile state leaks into the
  // NavBar / gated routes (e.g. the full patient nav shows above the login form
  // when `PublicRoute` declines to redirect an expired session away from
  // `/login`). Treat "expired" the same as "logged out" here.
  const hasValidSession = isAuthenticated && !shouldReLogin();

  // Hydrate synchronously from the persisted cache (once) so a reload with a
  // known token paints the resolved state immediately — no loader, no refetch.
  // hive-auth's cookie store makes `accessToken` available on the first render.
  const initialCacheReadRef = useRef(false);
  const initialCacheRef = useRef<CachedPatientProfile | null>(null);
  if (!initialCacheReadRef.current) {
    initialCacheReadRef.current = true;
    initialCacheRef.current = hasValidSession ? readCache(accessToken) : null;
  }
  const initialCache = initialCacheRef.current;

  const [profile, setProfile] = useState<PatientProfile | null>(
    initialCache?.profile ?? null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<PatientProfileStatus>(
    initialCache?.status ?? "loading"
  );
  // Guards against re-fetching for a token we've already resolved (including
  // one hydrated from the cache above). Cache expiry is enforced on mount/token
  // change via `readCache`, not by a background timer.
  const fetchedTokenRef = useRef<string | null>(
    initialCache ? accessToken ?? null : null
  );

  useEffect(() => {
    if (!hasValidSession || !accessToken) {
      setProfile(null);
      setError(null);
      setIsLoading(false);
      setStatus("loading");
      fetchedTokenRef.current = null;
      return undefined;
    }

    if (fetchedTokenRef.current === accessToken) {
      return undefined;
    }

    // Token changed (or resolved after first render) — reuse the persisted
    // cache before hitting the network so reloads stay offline.
    const cached = readCache(accessToken);
    if (cached) {
      fetchedTokenRef.current = accessToken;
      setProfile(cached.profile);
      setStatus(cached.status);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    fetchedTokenRef.current = accessToken;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setStatus("loading");

    const loadPatientProfile = async () => {
      try {
        const response = await patient(accessToken);
        if (cancelled) return;
        setProfile(response.data);
        setStatus("patient");
        writeCache({ accessToken, status: "patient", profile: response.data });
      } catch (err) {
        if (cancelled) return;
        setProfile(null);

        const httpStatus = axios.isAxiosError(err)
          ? err.response?.status
          : undefined;
        // Valid token, but this user is not a telehealth patient. We no longer
        // tear down the session here — enforcement lives in `RequirePatient`
        // (applied per-route), so routes like `/care` can stay open to them.
        if (httpStatus === 400 || httpStatus === 404) {
          setStatus("not-patient");
          writeCache({ accessToken, status: "not-patient", profile: null });
          return;
        }

        setError(
          err instanceof Error
            ? err
            : new Error("Failed to load patient profile")
        );
        setStatus("error");
        // Distinct from `not-patient` above, which is an expected outcome.
        // This one blocks every patient-gated route behind ProfileLoadError.
        trackDatadogAction(RUM_ACTIONS.PATIENT_PROFILE_LOAD_ERROR);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadPatientProfile();

    return () => {
      cancelled = true;
    };
  }, [hasValidSession, accessToken]);

  return (
    <PatientProfileContext.Provider
      value={{ profile, isLoading, error, status }}
    >
      {children}
    </PatientProfileContext.Provider>
  );
};

export const usePatientProfile = (): PatientProfileContextValue =>
  useContext(PatientProfileContext);
